import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Switch } from '@headlessui/react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { buildRestaurantPath, useApiClient } from '../lib/api';
import { useConfig } from '../providers/config-provider';
import { useAuth } from '../providers/auth-provider';
import { useNotification } from '../providers/notification-provider';
import StatusPill from '../components/common/StatusPill';
import Pagination from '../components/common/Pagination';
import SortableTh from '../components/common/SortableTh';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { AREAS_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Informe o nome da área')
    .max(100, 'Nome da área deve ter no máximo 100 caracteres'),
  isActive: z.boolean().optional()
});

const pageSize = 10;

const AreasPage = () => {
  const api = useApiClient();
  const config = useConfig();
  const { user } = useAuth();
  const { showToast, showConfirm } = useNotification();

  const canManageAreas = user?.role === 'Admin' || user?.role === 'Manager' || user?.role === 'AdminMaster';

  const [areas, setAreas] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [viewingArea, setViewingArea] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(AREAS_HELP_SECTIONS[0].key);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      isActive: true
    }
  });

  useEffect(() => {
    document.title = 'Áreas | Orion WaiterCall';
  }, []);

  const loadAreas = useCallback(async () => {
    if (!config.restaurantId) {
      setAreas([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(buildRestaurantPath(config.restaurantId, 'areas'), {
        params: {
          includeInactive,
          page,
          pageSize,
          search: searchTerm.trim() || undefined
        }
      });
      const payload = response.data;
      if (payload?.items) {
        setAreas(payload.items);
        setTotalItems(payload.totalItems ?? 0);
        setTotalPages(Math.max(1, payload.totalPages ?? 1));
      } else {
        const items = payload || [];
        setAreas(items);
        setTotalItems(items.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar as áreas.', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, config.restaurantId, includeInactive, page, searchTerm, showToast]);

  useEffect(() => {
    loadAreas();
  }, [loadAreas, config.restaurantId, config.companyId]);

  const openCreate = () => {
    if (!canManageAreas) {
      return;
    }

    setModalMode('create');
    setEditingId(null);
    reset({ name: '', isActive: true });
    setModalOpen(true);
  };

  const openEdit = (area) => {
    if (!canManageAreas) {
      return;
    }

    setModalMode('edit');
    setEditingId(area.id);
    reset({
      name: area.name,
      isActive: area.isActive
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
  };

  const onSubmit = async (values) => {
    if (!config.restaurantId) {
      showToast('Selecione um restaurante no topo.', 'warning');
      return;
    }

    const editingArea = modalMode === 'edit' ? areas.find((item) => item.id === editingId) : null;
    const willInactivate = modalMode === 'edit' && editingArea?.isActive && values.isActive === false;
    if (willInactivate) {
      const confirmed = await showConfirm({
        title: 'Inativar área',
        message: `Deseja inativar a área ${editingArea.name}?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        isActive: values.isActive ?? true
      };

      if (modalMode === 'edit' && editingId) {
        await api.put(buildRestaurantPath(config.restaurantId, `areas/${editingId}`), payload);
        showToast('Área atualizada com sucesso.', 'success');
      } else {
        await api.post(buildRestaurantPath(config.restaurantId, 'areas'), payload);
        showToast('Área cadastrada com sucesso.', 'success');
      }

      closeModal();
      loadAreas();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || error?.response?.data || 'Não foi possível salvar a área.';
      showToast(typeof message === 'string' ? message : 'Não foi possível salvar a área.', 'error');
      setSaving(false);
    }
  };

  const deleteArea = async (area) => {
    if (!config.restaurantId) {
      return;
    }

    const confirmed = await showConfirm({
      title: 'Excluir área',
      message: `Deseja excluir a área ${area.name}?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar'
    });

    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await api.delete(buildRestaurantPath(config.restaurantId, `areas/${area.id}`));
      showToast('Área excluída com sucesso.', 'success');
      loadAreas();
    } catch (error) {
      console.error(error);
      showToast('Não foi possível excluir a área.', 'error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const visibleAreas = useMemo(() => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const toText = (value) => String(value || '').trim().toLocaleLowerCase('pt-BR');

    return [...areas].sort((a, b) => {
      if (sortConfig.key === 'isActive') {
        if (a.isActive !== b.isActive) {
          return (a.isActive ? -1 : 1) * direction;
        }
        return toText(a.name).localeCompare(toText(b.name), 'pt-BR');
      }

      const comparison = toText(a.name).localeCompare(toText(b.name), 'pt-BR') * direction;
      if (comparison !== 0) {
        return comparison;
      }

      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  }, [areas, sortConfig]);

  return (
    <section>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Áreas</h1>
          <p className="mt-2 text-sm text-gray-700">Gerencie as áreas de atendimento do restaurante.</p>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:ml-16 sm:mt-0 sm:flex-row sm:items-center sm:flex-none">
          {canManageAreas ? (
            <button
              type="button"
              onClick={openCreate}
              className="block w-full rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto"
            >
              Nova área
            </button>
          ) : null}
          <ContextHelpButton onClick={() => setIsHelpOpen(true)} />
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
        <input
          type="text"
          placeholder="Buscar por nome da área..."
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setPage(1);
          }}
          className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 max-w-md"
        />
        <div className="w-full sm:w-auto">
          <div className="flex h-[38px] items-center gap-3">
            <Switch
              checked={!includeInactive}
              onChange={(checked) => {
                setIncludeInactive(!checked);
                setPage(1);
              }}
              className={`${!includeInactive ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
            >
              <span className="sr-only">Apenas ativas</span>
              <span
                aria-hidden="true"
                className={`${!includeInactive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <span className="text-sm text-gray-600">Apenas ativas</span>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:hidden space-y-3">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Carregando áreas...</div>
        ) : visibleAreas.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">Nenhuma área encontrada.</div>
        ) : (
          visibleAreas.map((area) => (
            <article key={area.id} onClick={() => setViewingArea(area)} className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{area.name}</h3>
                </div>
                <StatusPill label={area.isActive ? 'Ativa' : 'Inativa'} variant={area.isActive ? 'success' : 'muted'} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {canManageAreas ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openEdit(area);
                    }}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Editar
                  </button>
                ) : null}
                {canManageAreas ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteArea(area);
                    }}
                    disabled={saving}
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Excluir
                  </button>
                ) : null}
              </div>
            </article>
          ))
        )}
      </div>

      <div className="mt-8 hidden sm:block">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="bg-white p-4 text-sm text-gray-600">Carregando áreas...</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableTh
                        label="Nome"
                        sortKey="name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      />
                      <SortableTh
                        label="Status"
                        sortKey="isActive"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {visibleAreas.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="py-8 text-center text-sm text-gray-500">
                          Nenhuma área encontrada com os filtros atuais.
                        </td>
                      </tr>
                    ) : (
                      visibleAreas.map((area) => (
                        <tr key={area.id} onClick={() => setViewingArea(area)} className="cursor-pointer hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{area.name}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <StatusPill label={area.isActive ? 'Ativa' : 'Inativa'} variant={area.isActive ? 'success' : 'muted'} />
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            {canManageAreas ? (
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEdit(area);
                                }}
                                title="Editar"
                                className="mr-4 text-indigo-600 hover:text-indigo-900"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            ) : null}
                            {canManageAreas ? (
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  deleteArea(area);
                                }}
                                title="Excluir"
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {!loading && totalItems > 0 ? (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={totalItems}
                  pageSize={pageSize}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-4 max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-slate-900">{modalMode === 'edit' ? 'Editar área' : 'Cadastrar área'}</h3>
              <button type="button" onClick={closeModal} className="text-sm font-semibold text-slate-500 hover:text-slate-800">
                Fechar
              </button>
            </div>
            <form className="mt-4 space-y-3" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-800">Nome *</label>
                <input
                  type="text"
                  {...register('name')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {errors.name ? <p className="text-sm text-red-600">{errors.name.message}</p> : null}
              </div>

              {modalMode === 'edit' ? (
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Área ativa
                </label>
              ) : null}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-70"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {viewingArea ? (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-4 max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detalhes da área</h3>
                <p className="text-sm text-gray-500">Informações de cadastro e status</p>
              </div>
              <button type="button" onClick={() => setViewingArea(null)} className="text-sm font-semibold text-slate-500 hover:text-slate-800">
                Fechar
              </button>
            </div>

            <div className="mt-4 space-y-3 text-sm text-gray-700">
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Nome</p>
                <p className="text-base font-medium text-gray-900">{viewingArea.name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Status</p>
                <StatusPill label={viewingArea.isActive ? 'Ativa' : 'Inativa'} variant={viewingArea.isActive ? 'success' : 'muted'} />
              </div>
            </div>

            <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setViewingArea(null)}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Cadastro de Áreas"
        description="Revise como estruturar áreas para organizar mesas, filas de chamada e atendimento por setor."
        sections={AREAS_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

export default AreasPage;
